import Router from 'next/router';
import { ListingProductFilter, FilterValue, SearchParams } from '../types';
import { stringToInteger } from '@teko/utils';

export const onRedirect = (type: string, pathname: string, query: {}): void => {
  if (type === 'push') {
    Router.push({
      pathname: pathname,
      query: query,
    });
  } else if (type === 'replace') {
    Router.replace({
      pathname: pathname,
      query: query,
    });
  }
};

export const onSearch = (type: string, query: SearchParams): void => {
  const currentQuery = Router.query;
  const pathname = query.q
    ? '/search'
    : currentQuery.categoryId
    ? `/categories/${currentQuery.categoryId}`
    : Router.pathname;
  if (!Object.keys(query).length) {
    onRedirect(type, pathname, currentQuery.q ? { q: currentQuery.q } : {});
    return;
  }
  if (query.hasOwnProperty('_page') && query._page === currentQuery._page) {
    delete query._page;
  }
  if (currentQuery.q) {
    query = { ...{ q: currentQuery.q, ...query } };
  }
  onRedirect(type, pathname, query);
};

export const onSort = (_sort: string): void => {
  const queryStringObj = Router.query;
  let _order = queryStringObj._order ? queryStringObj._order : 'desc';
  if (_sort === 'bestPrice') {
    _order === 'desc' ? (_order = 'asc') : (_order = 'desc');
  }
  if (_sort) {
    onSearch('push', {
      ...queryStringObj,
      ...{
        _sort: _sort,
        _order: _order,
      },
    });
  } else {
    delete queryStringObj._sort;
    delete queryStringObj._order;
    onRedirect('push', Router.pathname, queryStringObj);
  }
};

export const getActivatedFilters = (
  filters: Array<ListingProductFilter>,
): Array<FilterValue> => {
  const queryStringObj = Router.query;
  const activatedFilters: Array<FilterValue> = [];
  Object.keys(queryStringObj).forEach((queryItem) => {
    filters.forEach((filter) => {
      if (filter.code === queryItem) {
        filter.values.forEach((value) => {
          if ((queryStringObj?.[queryItem] || '').includes(value.option)) {
            activatedFilters.push(value);
          }
        });
      }
    });
  });
  return activatedFilters;
};

export const removeFilter = (
  filterValue: FilterValue,
  activeFilters: FilterValue[],
): void => {
  const newActivatedFilters = activeFilters.filter((item) => {
    return item.option !== filterValue.option;
  });

  if (!newActivatedFilters.length) {
    onSearch('push', {});
    return;
  }
  const newActivatedFiltersQuery = newActivatedFilters.reduce(
    (obj: { [code: string]: Array<string> }, item) => {
      obj[item.code] = obj[item.code] || [];
      obj[item.code].push(item.option);
      return obj;
    },
    {},
  );
  const convertedFiltersQuery: Record<string, string> = {};
  Object.keys(newActivatedFiltersQuery).forEach((key) => {
    convertedFiltersQuery[key] = newActivatedFiltersQuery[key].join();
  });
  onSearch('push', convertedFiltersQuery);
};

export const convertFilters = (
  filters: ListingProductFilter[],
): ListingProductFilter[] => {
  const newFilters = filters.map((filter) => {
    const newValues = filter.values.map((item) => {
      return { ...item, ...{ code: filter.code, name: filter.name } };
    });
    filter.values = newValues;
    return filter;
  });
  return newFilters;
};

export const cookFilterPrice = (lte = 10000000, gte = 0) => {
  /**
   * default : gte: 0, lte: 10.000.000, step = 500.000, delta = lte - gte
   * lte: is rounded to 50k, 500K, 1M, 5M
   * step = (delta / 20) is rounded to 5k, 10k, 100K, 500K
   */
  lte = lte === null || isNaN(lte) ? 10000000 : lte;
  gte = gte === null || isNaN(gte) ? 0 : gte;
  let priceLte =
    stringToInteger(lte.toString()) ||
    stringToInteger(gte.toString(), 0) + 500000;
  const priceGte = 0;

  if (priceLte < 200000) {
    priceLte = Math.ceil(priceLte / 50000) * 50000;
  }

  if (priceLte > 200000 && priceLte < 500000) {
    priceLte = 500000;
  }

  if (priceLte > 500000 && priceLte < 5000000) {
    priceLte = Math.ceil(priceLte / 1000000) * 1000000;
  }
  if (priceLte > 5000000) {
    priceLte = Math.ceil(priceLte / 5000000) * 5000000;
  }

  const delta = priceLte - priceGte;

  let step = 100000;

  if (delta < 200000) {
    step = Math.ceil(delta / 100000) * 5000;
    /**minStep : 5.000d , maxStep: 10.000 */
  }
  if (delta > 200000 && delta <= 2000000) {
    step = Math.ceil(delta / 200000) * 10000;
    /**minStep : 20.000d , maxStep: 100.000 */
  }

  if (delta > 2000000 && delta <= 10000000) {
    step = Math.ceil(delta / 2000000) * 100000;
    /**minStep : 100.000d , maxStep: 500.000 */
  }
  if (delta > 10000000) {
    step = 500000;
  }

  // steps

  return {
    max: priceLte,
    min: priceGte,
    step: step,
  };
};
